// 扫描打印
import LogisticsCompany from "@/enum/LogisticsCompany";
import { get, post } from "@/services/http/platformServer";
import { PagedResult } from "../types";

interface QueryPackageExceptionParams {
    /**
     * 异常生成时间 - 结束时间。
     */
    createEndTime?: string;
    /**
     * 异常生成时间 - 开始时间。
     * <p>预留字段，目前默认近 7 天。
     */
    createStartTime?: string;
    /**
     * 一级异常类型。
     */
    exceptionCode: ExceptionCode;
    /**
     * 是否已发货。
     */
    hasShipped?: boolean;
    /**
     * 快递公司
     */
    logisticsCompany?: LogisticsCompany;
    /**
     * 订单编号
     */
    orderOriginalId?: string;
    /**
     * 页码
     */
    pageNo?: number;
    /**
     * 每页数据数。
     */
    pageSize?: number;
    /**
     * 支付时间-结束时间。
     */
    payEndTime?: string;
    /**
     * 支付时间-开始时间。
     * <p>支持查询近 30 天的支付时间。
     * <p>没有参数时，默认选择近 7 天的支付时间。
     */
    payStartTime?: string;
    /**
     * 商品编号
     */
    productOriginalId?: string;
    /**
     * 待查询的店铺 ID。
     */
    shopId: number;
    /**
     * 二级异常类型。
     */
    subExceptionCode?: SubExceptionCode;
    /**
     * 运单号
     */
    waybillNo?: string;
}

/**
 * 一级异常类型。
 */
export enum ExceptionCode {
    DELIVERY_UPDATE_EXCEPTION = "DELIVERY_UPDATE_EXCEPTION",
    GOT_EXCEPTION = "GOT_EXCEPTION",
    GOT_UPDATE_EXCEPTION = "GOT_UPDATE_EXCEPTION",
    TRANSPORT_EXCEPTION = "TRANSPORT_EXCEPTION",
}

/**
 * 二级异常类型。
 */
export enum SubExceptionCode {
    COLLECTED_STOP = "COLLECTED_STOP",
    CONSIGN_CLICKED_FAKE = "CONSIGN_CLICKED_FAKE",
    CONSIGN_DELAY = "CONSIGN_DELAY",
    DELIVERING_STOP = "DELIVERING_STOP",
    OUT_STOCK = "OUT_STOCK",
    TRANSPORTING_STOP = "TRANSPORTING_STOP",
    WILL_CONSIGN_FAKE_TIMEOUT = "WILL_CONSIGN_FAKE_TIMEOUT",
    WILL_DELIVERY_UPDATE_TIMEOUT = "WILL_DELIVERY_UPDATE_TIMEOUT",
    WILL_GOT_TIMEOUT = "WILL_GOT_TIMEOUT",
    WILL_GOT_UPDATE_TIMEOUT = "WILL_GOT_UPDATE_TIMEOUT",
    WILL_TRANSPORT_TIMEOUT = "WILL_TRANSPORT_TIMEOUT",
}

/**
 * 异常维度类型。
 */
export enum ExceptionDimensionType {
    OrderDimension = "ORDER_DIMENSION",
    WaybillDimension = "WAYBILL_DIMENSION",
}

/**
 * 异常类型。
 */
export enum ExceptionType {
    Exception = "EXCEPTION",
    Warning = "WARNING",
}

interface OrderDetails {
    /**
     * 交易子订单应揽收时间。
     */
    expectCollectTime?: string;
    /**
     * 物流公司。
     */
    logisticsCompany?: LogisticsCompany;
    /**
     * 订单项 ID。
     */
    orderItemOriginalId?: string;
    /**
     * 订单 ID。
     */
    orderOriginalId?: string;
    /**
     * 运单号。
     */
    waybillNo?: string;
}

interface WaybillDetails {
    /**
     * 物流公司。
     */
    logisticsCompany?: LogisticsCompany;
    /**
     * 运单号关联的订单 ID。
     */
    orderOriginalId?: string;
    /**
     * 运单号。
     */
    waybillNo?: string;
}

export interface QueryPackageExceptionResultItem {
    /**
     * 补偿金额，单位为分。
     */
    compensateAmount?: number;
    /**
     * 异常生成时间。
     */
    createTime?: string;
    /**
     * 一级异常类型。
     */
    exceptionCode?: ExceptionCode;
    /**
     * 异常维度类型。
     */
    exceptionDimensionType?: ExceptionDimensionType;
    /**
     * 异常 ID。
     */
    exceptionId?: string;
    /**
     * 异常类型。
     */
    exceptionType?: ExceptionType;
    /**
     * 订单维度异常信息。
     */
    orderDetails?: OrderDetails[];
    /**
     * 预警异常转实际实际异常时间，仅对预警型异常有效，表示当前预警类异常将变成真实异常的时间，该时间可能是未来时间也可能是过去时间。
     */
    overTime?: string;
    /**
     * 二级异常类型。
     */
    subExceptionCode?: SubExceptionCode;
    /**
     * 运单维度异常信息。
     */
    waybillDetails?: WaybillDetails[];
}

interface PackageExceptionQueryResponse {
    content: QueryPackageExceptionResultItem[];
    pageNo: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
}

/** 分页查询淘宝物流包裹异常信息。 */
export function queryPackageException(params: QueryPackageExceptionParams) {
    return post<PackageExceptionQueryResponse>(
        params.shopId,
        "/taobao/logistics/package/query_package_exception",
        params,
    );
}

export const exceptionCodeNameMap = {
    [ExceptionCode.GOT_EXCEPTION]: "揽收异常",
    [ExceptionCode.GOT_UPDATE_EXCEPTION]: "揽收更新异常",
    [ExceptionCode.TRANSPORT_EXCEPTION]: "运输异常",
    [ExceptionCode.DELIVERY_UPDATE_EXCEPTION]: "派送更新异常",
};

export const subExceptionCodeNameMap = {
    [SubExceptionCode.WILL_GOT_TIMEOUT]: "揽收即将超时",
    [SubExceptionCode.WILL_CONSIGN_FAKE_TIMEOUT]: "即将虚假发货",
    [SubExceptionCode.CONSIGN_DELAY]: "延迟发货",
    [SubExceptionCode.CONSIGN_CLICKED_FAKE]: "虚假点击发货",
    [SubExceptionCode.OUT_STOCK]: "缺货",
    [SubExceptionCode.WILL_GOT_UPDATE_TIMEOUT]: "揽收更新即将超时",
    [SubExceptionCode.COLLECTED_STOP]: "揽收后停滞",
    [SubExceptionCode.WILL_TRANSPORT_TIMEOUT]: "运输派送即将超时",
    [SubExceptionCode.TRANSPORTING_STOP]: "运输停滞",
    [SubExceptionCode.WILL_DELIVERY_UPDATE_TIMEOUT]: "派送更新即将超时",
    [SubExceptionCode.DELIVERING_STOP]: "派送停滞",
};

export interface ExceptionConfigQueryResponse {
    bizErrorCode?: string;
    bizErrorMsg?: string;
    data?: ExceptionConfigResult;
    retry?: boolean;
    success?: boolean;
}

export interface ExceptionConfigResult {
    collectedStopWarnValue?: number;
    consignDelayWarnValue?: number;
    consignFakeWarnValue?: number;
    deliveryingStopWarnValue?: number;
    transportingStopWarnValue?: number;
}

/** 查询淘宝商家物流包裹预警配置信息。 */
export async function queryPackageExceptionConfig(shopId: number) {
    const { data } = await post<ExceptionConfigQueryResponse>(
        shopId,
        "/taobao/logistics/package/query_package_exception_config",
        { shopId },
    );

    return data;
}
